import api from './api.service';
import { postExcelImportWithRetry } from '@/utils/excelImportUpload';

// Types
export interface BOQRequest {
  boqName: string;
  customerName?: string;
  customerId?: string;
  projectName?: string;
  projectId?: string;
  /** Omit or set null on update to clear CRM link */
  opportunityId?: string | null;
  /** Overrides opportunity-linked RST when set */
  revenueServiceTypeId?: string | null;
  boqDate?: string;
  validUntilDate?: string;
  currency?: string;
  marginPercentage?: number;
  notes?: string;
  categories?: CategoryRequest[];
  lineItems?: LineItemRequest[];
}

export interface CategoryRequest {
  categoryName: string;
  sequenceNumber?: number;
  lineItems?: LineItemRequest[];
}

export interface LineItemRequest {
  categoryId?: string;
  categoryName?: string;
  itemCode?: string;
  itemName: string;
  description?: string;
  revision?: string;
  quantity: number;
  uom: string;
  unitCost: number;
  costBasis?: string;
  supplierName?: string;
  leadTimeDays?: number;
  notes?: string;
  /** Item-level margin override (mirrors BOQ line item); used before save when no boqLineItem */
  marginPercentage?: number | null;
  /** Set when row is synced from persisted BOQ line item */
  lineItemId?: string;
  boqLineItem?: BOQLineItem;
}

export interface BOQHeader {
  id: string;
  company_id: string;
  boq_number: string;
  boq_name: string;
  customer_id?: string;
  customer_name?: string;
  project_id?: string;
  project_name?: string;
  opportunity_id?: string;
  revenue_service_type_id?: string | null;
  /** Present when API loads `opportunity` relation */
  opportunity?: {
    id: string;
    opportunity_number: string;
    opportunity_name: string;
  };
  boq_date: string;
  valid_until_date?: string;
  total_amount: number;
  margin_percentage: number;
  selling_price: number;
  currency: string;
  status: string;
  notes?: string;
  created_by: string;
  sales_team_id?: string | null;
  sales_team_name?: string | null;
  approved_by?: string;
  approved_at?: string;
  created_at: string;
  updated_at: string;
  categories?: BOQCategory[];
  lineItems?: BOQLineItem[];
}

export interface BOQCategory {
  id: string;
  boq_id: string;
  category_name: string;
  sequence_number: number;
  category_total: number;
  is_active: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
  lineItems?: BOQLineItem[];
}

export interface BOQLineItem {
  id: string;
  boq_id: string;
  category_id?: string;
  line_number: number;
  item_code?: string;
  item_name: string;
  description?: string;
  revision?: string;
  quantity: number;
  uom: string;
  unit_cost: number;
  line_total: number;
  cost_basis: string;
  supplier_id?: string;
  supplier_name?: string;
  lead_time_days: number;
  notes?: string;
  // New fields for materials & operations support
  margin_percentage?: number;
  material_cost: number;
  labor_cost: number;
  overhead_cost: number;
  machine_cost: number;
  tooling_cost: number;
  operation_outsourced_cost?: number;
  calculated_unit_cost: number;
  use_manual_cost: boolean;
  unit_selling_price: number;
  line_selling_total: number;
  created_at: string;
  updated_at: string;
  materials?: BOQLineItemMaterial[];
  operations?: BOQLineItemOperation[];
}

export interface BOQLineItemMaterial {
  id: string;
  boq_line_item_id: string;
  material_id?: string;
  description: string;
  quantity: number;
  uom: string;
  unit_cost: number;
  total_cost: number;
  cost_basis: string;
  supplier_id?: string;
  supplier_name?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface BOQLineItemOperation {
  id: string;
  boq_line_item_id: string;
  operation_id?: string;
  description: string;
  sequence_number: number;
  setup_time_hours: number;
  run_time_per_unit_hours: number;
  /** Legacy DB column; costing uses setup + run hours only (always 1). */
  quantity?: number;
  labor_rate_per_hour: number;
  overhead_rate_per_hour: number;
  machine_rate_per_hour: number;
  tooling_cost: number;
  total_cost: number;
  is_outsourced: boolean;
  outsource_cost?: number;
  notes?: string;
  /** Vendor-facing scope when outsourced (seeds PR specifications) */
  outsourced_scope_detail?: string | null;
  /** Supplier category UUIDs when outsourced (procurement shortlist) */
  outsourced_supplier_category_ids?: string[];
  created_at: string;
  updated_at: string;
}

export interface MaterialRequest {
  materialId?: string | null;
  description: string;
  quantity: number;
  uom: string;
  unitCost: number;
  costBasis?: string;
  supplierId?: string;
  supplierName?: string;
  notes?: string;
}

export interface OperationRequest {
  operationId?: string;
  description: string;
  sequenceNumber?: number;
  setupTimeHours?: number;
  runTimePerUnitHours?: number;
  laborRatePerHour?: number;
  overheadRatePerHour?: number;
  machineRatePerHour?: number;
  toolingCost?: number;
  isOutsourced?: boolean;
  outsourceCost?: number;
  notes?: string;
  /** Vendor-facing scope when outsourced */
  outsourcedScopeDetail?: string | null;
  outsourcedSupplierCategoryIds?: string[];
}

export interface CostBreakdown {
  materialCost: number;
  laborCost: number;
  overheadCost: number;
  toolingCost: number;
  calculatedUnitCost: number;
  manualUnitCost: number;
  useManualCost: boolean;
  finalUnitCost: number;
  marginPercentage: number;
  unitSellingPrice: number;
  lineTotal: number;
  lineSellingTotal: number;
}

/** GET /estimations/boq/line-items/pricing-history */
export interface BoqLineItemPricingHistoryRow {
  line_item_id: string;
  boq_id: string;
  boq_number: string;
  boq_name: string;
  item_code?: string;
  item_name: string;
  unit_cost: number;
  calculated_unit_cost: number;
  use_manual_cost: boolean;
  updated_at: string;
  material_count: number;
  operation_count: number;
}

export interface ImportExcelRequest {
  file: File;
  boqName: string;
  customerName?: string;
  customerId?: string;
  projectId?: string;
  projectName?: string;
  currency?: string;
  opportunityId?: string;
  /** Inherited from opportunity when importing against CRM; optional override. */
  salesTeamId?: string;
}

export interface ImportResult {
  success: boolean;
  boqId?: string;
  message: string;
  errors?: string[];
  statistics?: {
    totalRows: number;
    validRows: number;
    categories: number;
    items: number;
  };
}

class BOQService {
  private baseURL = '/estimations/boq';

  /**
   * Create new BOQ
   */
  async create(data: BOQRequest) {
    const response = await api.post(`${this.baseURL}`, data);
    return response.data;
  }

  /**
   * Get all BOQs with filters
   */
  async getAll(filters?: {
    customerId?: string;
    opportunity_id?: string;
    status?: string;
    fromDate?: string;
    toDate?: string;
    search?: string;
    sales_team_id?: string;
  }) {
    const response = await api.get(`${this.baseURL}`, { params: filters });
    return response.data;
  }

  /**
   * Get BOQ by ID
   */
  async getById(id: string) {
    const response = await api.get(`${this.baseURL}/${id}`);
    // Backend returns { success: true, data: {...} }
    return response.data;
  }

  /**
   * Update BOQ
   */
  async update(id: string, data: Partial<BOQRequest>) {
    const response = await api.put(`${this.baseURL}/${id}`, data);
    return response.data;
  }

  /**
   * Update BOQ status
   */
  async updateStatus(id: string, status: string) {
    const response = await api.put(`${this.baseURL}/${id}/status`, { status });
    return response.data;
  }

  /**
   * Submit BOQ for review
   */
  async submitForReview(id: string) {
    const response = await api.post(`${this.baseURL}/${id}/submit`);
    return response.data;
  }

  /**
   * Approve BOQ
   */
  async approve(id: string) {
    const response = await api.post(`${this.baseURL}/${id}/approve`);
    return response.data;
  }

  /**
   * Reject BOQ
   */
  async reject(id: string, reason?: string) {
    const response = await api.post(`${this.baseURL}/${id}/reject`, { reason });
    return response.data;
  }

  /**
   * Delete BOQ
   */
  async delete(id: string) {
    const response = await api.delete(`${this.baseURL}/${id}`);
    return response.data;
  }

  /**
   * Add line items to BOQ
   */
  async addLineItems(boqId: string, items: LineItemRequest[]) {
    const response = await api.post(`${this.baseURL}/${boqId}/items`, { items });
    return response.data;
  }

  /**
   * Update line item
   */
  async updateLineItem(boqId: string, itemId: string, data: Partial<LineItemRequest>) {
    const response = await api.put(`${this.baseURL}/${boqId}/items/${itemId}`, data);
    return response.data;
  }

  /**
   * Delete line item
   */
  async deleteLineItem(boqId: string, itemId: string) {
    const response = await api.delete(`${this.baseURL}/${boqId}/items/${itemId}`);
    return response.data;
  }

  /**
   * Recalculate BOQ totals
   */
  async recalculate(boqId: string) {
    const response = await api.post(`${this.baseURL}/${boqId}/calculate`);
    return response.data;
  }

  /**
   * Duplicate BOQ
   */
  async duplicate(boqId: string) {
    const response = await api.post(`${this.baseURL}/${boqId}/duplicate`);
    return response.data;
  }

  /**
   * Import BOQ from Excel
   * Uses postForm so the request is sent as multipart/form-data with correct boundary.
   */
  async importExcel(request: ImportExcelRequest): Promise<ImportResult> {
    const client = api.getClient();
    const formPayload = new FormData();
    formPayload.append('file', request.file);
    formPayload.append('boqName', request.boqName);
    if (request.customerName != null && request.customerName !== '') {
      formPayload.append('customerName', request.customerName);
    }
    if (request.customerId != null && request.customerId !== '') {
      formPayload.append('customerId', request.customerId);
    }
    if (request.projectId != null && request.projectId !== '') {
      formPayload.append('projectId', request.projectId);
    }
    if (request.projectName != null && request.projectName !== '') {
      formPayload.append('projectName', request.projectName);
    }
    if (request.currency != null && request.currency !== '') {
      formPayload.append('currency', request.currency);
    }
    if (request.opportunityId != null && request.opportunityId !== '') {
      formPayload.append('opportunity_id', request.opportunityId);
    }
    if (request.salesTeamId != null && request.salesTeamId !== '') {
      formPayload.append('sales_team_id', request.salesTeamId);
    }

    console.log('[BOQService] Import request:', {
      boqName: request.boqName,
      customerId: request.customerId,
      customerName: request.customerName,
      projectId: request.projectId,
      projectName: request.projectName,
      currency: request.currency,
      opportunityId: request.opportunityId,
      salesTeamId: request.salesTeamId,
      file: { name: request.file.name, size: request.file.size, type: request.file.type }
    });

    try {
      const response = await postExcelImportWithRetry(client, `${this.baseURL}/import`, formPayload, {
        // Excel parsing + DB inserts can exceed default 30s on larger BOQs.
        timeout: 180000,
      });
      console.log('[BOQService] Import response:', response.data);
      // Backend wraps in { success, data, message }; return the inner result (ImportResult)
      return (response.data as { data?: ImportResult })?.data ?? (response.data as ImportResult);
    } catch (error: any) {
      console.error('[BOQService] Import error:', error);
      if (error?.response) {
        console.error('[BOQService] Error response:', error.response?.data);
      } else {
        console.error('[BOQService] No HTTP response (network/CORS/timeout/offline):', {
          code: error?.code,
          message: error?.message
        });
      }
      throw error;
    }
  }

  /**
   * Export BOQ to Excel
   */
  async exportExcel(boqId: string): Promise<Blob> {
    const response = await api.get(`${this.baseURL}/${boqId}/export`, {
      responseType: 'blob'
    });
    return response.data;
  }

  /**
   * Download Excel file
   */
  downloadExcelFile(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // ========================================
  // Materials Management
  // ========================================

  /**
   * Add material to BOQ line item
   */
  async addMaterial(boqId: string, lineItemId: string, material: MaterialRequest) {
    const response = await api.post(`${this.baseURL}/${boqId}/line-items/${lineItemId}/materials`, material);
    return response.data;
  }

  /**
   * Get all materials for a BOQ line item
   */
  async getMaterials(boqId: string, lineItemId: string): Promise<BOQLineItemMaterial[]> {
    const response = await api.get(`${this.baseURL}/${boqId}/line-items/${lineItemId}/materials`);
    return response.data.data || response.data;
  }

  /**
   * Update material
   */
  async updateMaterial(boqId: string, lineItemId: string, materialId: string, material: Partial<MaterialRequest>) {
    const response = await api.put(`${this.baseURL}/${boqId}/line-items/${lineItemId}/materials/${materialId}`, material);
    return response.data;
  }

  /**
   * Delete material
   */
  async deleteMaterial(boqId: string, lineItemId: string, materialId: string) {
    const response = await api.delete(`${this.baseURL}/${boqId}/line-items/${lineItemId}/materials/${materialId}`);
    return response.data;
  }

  // ========================================
  // Operations Management
  // ========================================

  /**
   * Add operation to BOQ line item
   */
  async addOperation(boqId: string, lineItemId: string, operation: OperationRequest) {
    const response = await api.post(`${this.baseURL}/${boqId}/line-items/${lineItemId}/operations`, operation);
    return response.data;
  }

  /**
   * Get all operations for a BOQ line item
   */
  async getOperations(boqId: string, lineItemId: string): Promise<BOQLineItemOperation[]> {
    const response = await api.get(`${this.baseURL}/${boqId}/line-items/${lineItemId}/operations`);
    return response.data.data || response.data;
  }

  /**
   * Update operation
   */
  async updateOperation(boqId: string, lineItemId: string, operationId: string, operation: Partial<OperationRequest>) {
    const response = await api.put(`${this.baseURL}/${boqId}/line-items/${lineItemId}/operations/${operationId}`, operation);
    return response.data;
  }

  /**
   * Delete operation
   */
  async deleteOperation(boqId: string, lineItemId: string, operationId: string) {
    const response = await api.delete(`${this.baseURL}/${boqId}/line-items/${lineItemId}/operations/${operationId}`);
    return response.data;
  }

  // ========================================
  // Cost Calculation
  // ========================================

  /**
   * Recalculate line item costs
   */
  async recalculateLineItem(boqId: string, lineItemId: string): Promise<CostBreakdown> {
    const response = await api.post(`${this.baseURL}/${boqId}/line-items/${lineItemId}/recalculate`);
    return response.data.data || response.data;
  }

  /**
   * Toggle manual cost override
   */
  async toggleManualCost(boqId: string, lineItemId: string, useManual: boolean) {
    const response = await api.post(`${this.baseURL}/${boqId}/line-items/${lineItemId}/toggle-manual-cost`, { useManual });
    return response.data;
  }

  /**
   * Update line item margin
   */
  async updateLineItemMargin(boqId: string, lineItemId: string, marginPercentage: number | null) {
    const response = await api.put(`${this.baseURL}/${boqId}/line-items/${lineItemId}/margin`, { marginPercentage });
    return response.data;
  }

  /**
   * Get line item cost breakdown
   */
  async getCostBreakdown(boqId: string, lineItemId: string): Promise<CostBreakdown> {
    const response = await api.get(`${this.baseURL}/${boqId}/line-items/${lineItemId}/cost-breakdown`);
    return response.data.data || response.data;
  }

  /**
   * Search past BOQ line items in the company (for pricing history)
   */
  async searchLineItemPricingHistory(params: {
    search: string;
    excludeLineItemId?: string;
    limit?: number;
  }): Promise<BoqLineItemPricingHistoryRow[]> {
    const response = await api.get(`${this.baseURL}/line-items/pricing-history`, {
      params: {
        search: params.search,
        excludeLineItemId: params.excludeLineItemId,
        limit: params.limit
      }
    });
    const d = (response.data as { data?: BoqLineItemPricingHistoryRow[] })?.data ?? response.data;
    return Array.isArray(d) ? d : [];
  }

  /**
   * Copy materials, operations, and line cost fields from a source line
   */
  async copyLineItemPricingFromSource(boqId: string, lineItemId: string, sourceLineItemId: string) {
    const response = await api.post(`${this.baseURL}/${boqId}/line-items/${lineItemId}/copy-pricing`, {
      sourceLineItemId
    });
    return (response.data as { data?: BOQHeader })?.data ?? (response.data as BOQHeader);
  }

  /** Copy Opportunity attachments not yet on this BOQ. */
  async syncOpportunityDocuments(boqId: string): Promise<{ copied: number }> {
    const response = await api.post<{ copied: number }>(`${this.baseURL}/${boqId}/sync-opportunity-documents`, {});
    const n = Number((response.data as { copied?: number } | undefined)?.copied);
    return { copied: Number.isFinite(n) ? n : 0 };
  }
}

export const boqService = new BOQService();
export default boqService;

